import { ContractPartyCompany } from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'

function normalize(company: string): string {
  return company.trim().toLocaleLowerCase()
}

export function emptyContractPartyCompany(company = ''): ContractPartyCompany {
  return {
    uuid: null,
    certificateUuid: null,
    company,
    country: null,
    euatCheckResult: null,
    createdOn: null,
    createdBy: null,
    editedOn: null,
    editedBy: null
  }
}

export function sortContractPartyCompanies(rows: ContractPartyCompany[]): ContractPartyCompany[] {
  return [...rows].sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }))
}

/**
 * Tells a new Contract Parties text apart from an edit of the current one: the text counts as replaced when
 * none of the saved companies occurs in it anymore (or there are none yet). Names are matched case-insensitively.
 */
export function isReplacedContractPartiesText(text: string, saved: ContractPartyCompany[]): boolean {
  const normalizedText = normalize(text)
  return saved.every(row => !normalizedText.includes(normalize(row.company)))
}

/**
 * Adds the freshly extracted company names to the given rows, e.g. ones the user added while the extraction
 * was still running. Existing rows are kept as they are; only names not yet present are appended. Names are
 * compared case-insensitively and the result is sorted alphabetically.
 */
export function mergeExtractedCompanies(rows: ContractPartyCompany[], companies: string[]): ContractPartyCompany[] {
  const known = new Set(rows.map(row => normalize(row.company)))
  const added: ContractPartyCompany[] = []
  for (const company of companies) {
    const name = company.trim()
    const key = normalize(name)
    if (name === '' || known.has(key)) {
      continue
    }
    known.add(key)
    added.push(emptyContractPartyCompany(name))
  }
  return sortContractPartyCompanies([...rows, ...added])
}

/** Trims the company names and drops the rows without one, as the rows are stored on confirm. */
export function normalizeContractPartyCompanies(rows: ContractPartyCompany[]): ContractPartyCompany[] {
  return rows
    .map(row => ({ ...row, company: row.company.trim() }))
    .filter(row => row.company !== '')
}

/**
 * Whether the edited rows differ from the saved ones in what the user can change: which rows exist, their company
 * and their country. The order of the rows does not matter.
 */
export function hasContractPartyChanges(edited: ContractPartyCompany[], saved: ContractPartyCompany[]): boolean {
  const toKey = (rows: ContractPartyCompany[]): string => sortContractPartyCompanies(normalizeContractPartyCompanies(rows))
    .map(row => JSON.stringify([row.uuid, row.company, row.country]))
    .join('\n')
  return toKey(edited) !== toKey(saved)
}
